/**
 * Homepage hero slideshow.
 *
 * Wraps the Hero section and the image banners that follow it into one
 * auto-advancing carousel at runtime. The sections themselves are never
 * modified, so the hero keeps its video, parallax and entry animation and each
 * banner keeps its own settings; deleting this script restores the original
 * stacked layout exactly.
 *
 * Banners can be targeted per device ("Show on" in the section settings). A
 * banner hidden that way is display:none, so it drops out of the flex track and
 * out of the slide count — which means the slide list is viewport-dependent and
 * has to be recomputed whenever the viewport crosses the breakpoint.
 */
(() => {
  if (typeof window === 'undefined' || window.__tathaHeroSlideshow) return;
  window.__tathaHeroSlideshow = true;

  /* In the theme editor every section must stay individually selectable. */
  if (window.Shopify && window.Shopify.designMode) return;

  const INTERVAL_MS = 3000;
  const SWIPE_THRESHOLD = 40;

  const build = () => {
    const main = document.getElementById('MainContent');
    if (!main) return;

    const heroEl = main.querySelector('.hero');
    if (!heroEl) return;
    const heroSection = heroEl.closest('.shopify-section');
    if (!heroSection) return;

    /* Take the hero plus every image banner directly after it. Anything else
       ends the run, so reordering the homepage disables this rather than
       swallowing an unrelated section. */
    const panels = [heroSection];
    let next = heroSection.nextElementSibling;
    while (next && next.classList.contains('shopify-section') && next.querySelector('.banner')) {
      panels.push(next);
      next = next.nextElementSibling;
    }
    if (panels.length < 2) return;

    const wrap = document.createElement('div');
    wrap.className = 'tatha-slideshow';
    wrap.setAttribute('role', 'region');
    wrap.setAttribute('aria-roledescription', 'carousel');
    wrap.setAttribute('aria-label', 'Featured');

    const track = document.createElement('div');
    track.className = 'tatha-slideshow__track';
    wrap.appendChild(track);

    heroSection.parentNode.insertBefore(wrap, heroSection);
    panels.forEach((p) => {
      p.classList.add('tatha-slideshow__slide');
      track.appendChild(p);
    });

    /* Scroll-reveal watches vertical intersection; a slide parked off to the
       side never "enters" and would slide in blank. Cancel it inside here. */
    wrap.querySelectorAll('.scroll-trigger').forEach((node) => {
      node.classList.remove('scroll-trigger--offscreen');
      node.classList.add('scroll-trigger--cancel');
    });

    const dots = document.createElement('div');
    dots.className = 'tatha-slideshow__dots';
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Choose slide');
    wrap.appendChild(dots);

    const videos = panels.map((p) => p.querySelector('video'));

    let shown = [];
    let index = 0;
    let timer = 0;
    let paused = false;

    const isVisible = (el) => window.getComputedStyle(el).display !== 'none';

    const renderDots = () => {
      if (dots.childElementCount === shown.length) return;
      dots.textContent = '';
      shown.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tatha-slideshow__dot';
        b.setAttribute('role', 'tab');
        b.setAttribute('aria-label', 'Slide ' + (i + 1) + ' of ' + shown.length);
        b.addEventListener('click', () => {
          goTo(i);
          restart();
        });
        dots.appendChild(b);
      });
      /* A single slide has nothing to choose between. */
      dots.hidden = shown.length < 2;
    };

    const render = () => {
      track.style.transform = 'translateX(' + -100 * index + '%)';

      panels.forEach((p) => {
        const active = shown[index] === p;
        /* inert keeps links on an inactive or device-hidden slide out of the
           tab order; aria-hidden keeps it out of the accessibility tree. */
        if ('inert' in p) p.inert = !active;
        p.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      dots.querySelectorAll('.tatha-slideshow__dot').forEach((d, i) => {
        d.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      /* Don't decode the hero video while it is off-screen. */
      videos.forEach((v, i) => {
        if (!v) return;
        try {
          if (shown[index] === panels[i]) {
            const r = v.play();
            if (r && typeof r.catch === 'function') r.catch(() => {});
          } else {
            v.pause();
          }
        } catch (e) {
          /* ignore */
        }
      });
    };

    const goTo = (i) => {
      if (!shown.length) return;
      index = (i + shown.length) % shown.length;
      render();
    };

    const advance = () => goTo(index + 1);

    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = 0;
    };

    const start = () => {
      stop();
      if (paused || shown.length < 2) return;
      timer = window.setInterval(advance, INTERVAL_MS);
    };

    const restart = () => {
      stop();
      start();
    };

    const setPaused = (v) => {
      paused = v;
      if (v) stop();
      else start();
    };

    /* Recompute which panels are on screen for this viewport. */
    const refresh = () => {
      const was = shown[index];
      shown = panels.filter(isVisible);
      if (!shown.length) {
        stop();
        return;
      }
      const keep = shown.indexOf(was);
      index = keep >= 0 ? keep : 0;
      renderDots();
      render();
      start();
    };

    /* Pause controls — a 3s auto-rotator needs a way to stop (WCAG 2.2.2). */
    wrap.addEventListener('mouseenter', () => setPaused(true));
    wrap.addEventListener('mouseleave', () => setPaused(false));
    wrap.addEventListener('focusin', () => setPaused(true));
    wrap.addEventListener('focusout', () => setPaused(false));
    document.addEventListener('visibilitychange', () => setPaused(document.hidden));

    /* Swipe */
    let x0 = null;
    wrap.addEventListener(
      'touchstart',
      (e) => {
        x0 = e.touches && e.touches.length ? e.touches[0].clientX : null;
      },
      { passive: true }
    );
    wrap.addEventListener(
      'touchend',
      (e) => {
        if (x0 === null) return;
        const t = e.changedTouches && e.changedTouches[0];
        if (t) {
          const dx = t.clientX - x0;
          if (Math.abs(dx) > SWIPE_THRESHOLD) {
            goTo(index + (dx < 0 ? 1 : -1));
            restart();
          }
        }
        x0 = null;
      },
      { passive: true }
    );

    /* Crossing the 750px breakpoint swaps the desktop banner for the mobile one
       (or back), which changes the slide count — so rebuild on resize. */
    let resizeTimer = 0;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(refresh, 150);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    shown = panels.filter(isVisible);
    renderDots();
    render();

    /* The intro loader covers the page for its first moments — start counting
       once it is gone so slide one actually gets its full interval on screen. */
    if (document.getElementById('HomeLoader')) {
      document.addEventListener('homeLoader:complete', start, { once: true });
      /* Safety net in case the loader is removed without firing the event. */
      window.setTimeout(() => {
        if (!timer && !paused) start();
      }, 5000);
    } else {
      start();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build, { once: true });
  } else {
    build();
  }
})();
